import { renderTemplateContent } from "../contentRenderer";
import { mergeTemplateDesign, templateDesignToCssVars } from "../design";
import { DefaultTemplateLink } from "../templateLink";
import type {
    ResolvedTemplateDesign,
    SiteTemplateDefinition,
    TemplateLinkComponent,
    TemplateRenderProps,
} from "../types";
import { WebBuilderPageRegion } from "../WebBuilderPageRegion";
import { WebGlobalBuilderRegion } from "../WebGlobalBuilderRegion";
import { webSystemTemplateDesignDefaults } from "../webSystemTemplateDesign";
import { TorqueServiceMobileMenu } from "./TorqueServiceMobileMenu";

function TorqueServiceStyles() {
    return (
        <style>{`
            [data-web-template="torque-service"] {
                --torque-accent-on-light: color-mix(in srgb, var(--site-accent) 72%, var(--site-primary));
                background: var(--site-background);
                color: var(--site-text);
                isolation: isolate;
                scroll-behavior: smooth;
                scroll-padding-top: 112px;
            }

            [data-web-template="torque-service"] ::selection {
                background: var(--site-accent);
                color: var(--site-on-accent);
            }

            [data-web-template="torque-service"] :focus-visible {
                outline: 3px solid var(--site-accent);
                outline-offset: 4px;
            }

            [data-web-template="torque-service"] *,
            [data-web-template="torque-service"] *::before,
            [data-web-template="torque-service"] *::after {
                box-sizing: border-box;
            }

            [data-web-template="torque-service"] .site-builder-section,
            [data-web-template="torque-service"] [data-site-builder-block="footer"] {
                padding-inline: clamp(1.25rem, 2vw, 2rem);
                position: relative;
            }

            [data-web-template="torque-service"] .site-builder-grid {
                display: grid;
                gap: clamp(1.25rem, 2.8vw, 2.65rem);
                margin-inline: auto;
                width: min(100%, var(--site-container));
            }

            [data-web-template="torque-service"] .site-builder-grid:not(.site-builder-grid-placed) {
                grid-template-columns: repeat(12, minmax(0, 1fr));
            }

            [data-web-template="torque-service"] .site-builder-grid:not(.site-builder-grid-placed) > .site-builder-grid-item {
                grid-column: 1 / -1;
            }

            [data-web-template="torque-service"] .site-builder-column {
                align-content: start;
                display: grid;
                gap: 1.15rem;
                min-width: 0;
            }

            [data-web-template="torque-service"] .site-builder-column-inline {
                align-items: center;
                display: flex;
                flex-wrap: wrap;
            }

            [data-web-template="torque-service"] .site-builder-text-h1,
            [data-web-template="torque-service"] .site-builder-text-h2,
            [data-web-template="torque-service"] .site-builder-text-h3 {
                font-stretch: condensed;
                text-wrap: balance;
            }

            [data-web-template="torque-service"] .site-builder-text-h1 {
                line-height: .78 !important;
            }

            [data-web-template="torque-service"] .site-builder-text-h2 {
                line-height: .84 !important;
            }

            [data-web-template="torque-service"] .site-builder-button {
                align-items: center;
                letter-spacing: .115em;
                min-height: 54px;
                text-transform: uppercase;
                transition: background-color 180ms ease, border-color 180ms ease, color 180ms ease, transform 180ms ease;
            }

            [data-web-template="torque-service"] .site-builder-button:hover {
                transform: translateX(4px);
            }

            [data-web-template="torque-service"] .site-builder-card {
                transition: border-color 220ms ease, box-shadow 220ms ease, transform 220ms ease;
            }

            [data-web-template="torque-service"] .site-builder-card:hover {
                border-color: color-mix(in srgb, var(--site-accent) 62%, var(--site-border)) !important;
                box-shadow: 0 18px 45px rgb(17 19 21 / .08);
                transform: translateY(-3px);
            }

            [data-web-template="torque-service"] .site-builder-navigation {
                align-items: center;
                display: flex;
                flex-wrap: wrap;
                gap: clamp(.5rem, .85vw, .85rem);
                justify-content: center;
            }

            [data-web-template="torque-service"] [data-document-role="headerNavigation"] {
                gap: clamp(.4rem, .7vw, .7rem) !important;
            }

            [data-web-template="torque-service"] [data-document-role="headerNavigation"] .site-navigation-primary {
                gap: clamp(.1rem, .35vw, .35rem);
            }

            [data-web-template="torque-service"] [data-document-role="headerNavigation"] .site-navigation-root {
                --site-navigation-accent: var(--torque-accent-on-light);
            }

            [data-web-template="torque-service"] [data-document-role="headerNavigation"] .site-navigation-primary-trigger,
            [data-web-template="torque-service"] [data-document-role="headerNavigation"] .site-navigation-primary-link {
                font-size: .7rem;
                padding-inline: clamp(.3rem, .55vw, .5rem);
            }

            [data-web-template="torque-service"] .site-builder-navigation-item {
                display: inline-flex;
            }

            [data-web-template="torque-service"] .site-builder-navigation-link,
            [data-web-template="torque-service"] [data-document-role="headerNavigation"] .site-builder-text {
                display: inline-flex;
                position: relative;
                width: auto !important;
            }

            [data-web-template="torque-service"] .site-builder-navigation-link::after,
            [data-web-template="torque-service"] [data-document-role="headerNavigation"] .site-builder-text::after {
                background: var(--site-accent);
                bottom: -.48rem;
                content: "";
                height: 2px;
                left: 0;
                position: absolute;
                right: 0;
                transform: scaleX(0);
                transform-origin: right;
                transition: transform 180ms ease;
            }

            [data-web-template="torque-service"] .site-builder-navigation-link:hover::after,
            [data-web-template="torque-service"] .site-builder-navigation-link[aria-current="page"]::after,
            [data-web-template="torque-service"] [data-document-role="headerNavigation"] .site-builder-text:hover::after {
                transform: scaleX(1);
                transform-origin: left;
            }

            [data-web-template="torque-service"] [data-web-region="serviceStrip"] {
                color: var(--site-surface);
                left: 0;
                padding-inline: clamp(1rem, 2vw, 2rem);
                position: sticky;
                right: 0;
                top: 0;
                z-index: 62;
            }

            [data-web-template="torque-service"] [data-web-region="serviceStrip"] > .site-builder-grid {
                align-items: center;
            }

            [data-web-template="torque-service"] [data-web-region="header"] {
                color: var(--site-primary);
                left: 0;
                padding-inline: clamp(1rem, 2vw, 2rem);
                position: sticky;
                right: 0;
                top: 36px;
                transition: box-shadow 220ms ease, background-color 220ms ease;
                z-index: 60;
            }

            [data-web-template="torque-service"] [data-web-region="header"][data-torque-header-scrolled] {
                box-shadow: 0 10px 34px rgb(17 19 21 / .12);
            }

            [data-web-template="torque-service"] [data-web-region="header"] > .site-builder-grid {
                align-items: center;
            }

            [data-web-template="torque-service"] [data-document-role="brand"] {
                clip-path: polygon(0 0, calc(100% - 20px) 0, 100% 50%, calc(100% - 20px) 100%, 0 100%);
                color: var(--site-surface);
                margin-left: clamp(-2rem, -2vw, -1rem);
                padding-right: clamp(2.25rem, 3.5vw, 3.5rem) !important;
            }

            [data-web-template="torque-service"] [data-document-role="headerActions"] {
                flex-wrap: nowrap;
                padding-left: clamp(.5rem, 1vw, 1rem);
                justify-content: flex-end;
            }

            [data-web-template="torque-service"] [data-document-role="headerCta"],
            [data-web-template="torque-service"] [data-document-role="localeActions"] {
                flex: 0 0 auto;
            }

            [data-web-template="torque-service"] [data-document-role="headerCta"] {
                color: var(--site-on-accent) !important;
                font-size: 12px !important;
                min-width: 0 !important;
                padding-inline: clamp(.85rem, 1.5vw, 1.35rem) !important;
            }

            [data-web-template="torque-service"] [data-document-role="accentAction"],
            [data-web-template="torque-service"] [data-web-region="contactForm"] .site-builder-form .site-builder-button {
                color: var(--site-on-accent) !important;
            }

            [data-web-template="torque-service"] [data-web-region="homeHero"] {
                overflow: hidden;
                padding-inline: 0;
            }

            [data-web-template="torque-service"] [data-web-region="homeHero"] > .site-builder-grid {
                gap: 0 !important;
                grid-auto-rows: minmax(0, auto) !important;
                grid-template-columns: minmax(0, .92fr) minmax(520px, 1.08fr) !important;
                max-width: none;
                width: 100%;
            }

            [data-web-template="torque-service"] [data-web-region="homeHero"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="heroCopy"]) {
                grid-column: 1 !important;
                grid-row: 1 !important;
            }

            [data-web-template="torque-service"] [data-web-region="homeHero"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="heroVisual"]) {
                grid-column: 2 !important;
                grid-row: 1 !important;
            }

            [data-web-template="torque-service"] [data-document-role="heroCopy"] {
                padding-left: max(1.25rem, calc((100cqw - var(--site-container)) / 2));
            }

            [data-web-template="torque-service"] [data-document-role="heroCopy"]:has(> [data-document-role="heroHeading"]) {
                align-content: center;
                column-gap: .75rem;
                display: grid;
                grid-template-columns: repeat(12, minmax(0, 1fr));
                row-gap: 1.15rem;
            }

            [data-web-template="torque-service"] [data-document-role="heroCopy"] > :not([data-document-role="heroPrimaryAction"], [data-document-role="heroSecondaryAction"]) {
                grid-column: 1 / -1;
            }

            [data-web-template="torque-service"] [data-document-role="heroPrimaryAction"] {
                grid-column: 1 / span 6;
            }

            [data-web-template="torque-service"] [data-document-role="heroSecondaryAction"] {
                grid-column: 7 / span 6;
            }

            [data-web-template="torque-service"] [data-document-role="heroVisual"] {
                isolation: isolate;
                overflow: hidden;
                position: relative;
            }

            [data-web-template="torque-service"] [data-document-role="heroVisual"]::before {
                background: linear-gradient(90deg, rgb(0 0 0 / .26), transparent 46%), linear-gradient(0deg, rgb(0 0 0 / .42), transparent 52%);
                content: "";
                inset: 0;
                pointer-events: none;
                position: absolute;
                z-index: 1;
            }

            [data-web-template="torque-service"] [data-document-role="heroVisual"]::after {
                background: var(--site-accent);
                bottom: -1px;
                clip-path: polygon(0 62%, 100% 0, 68% 100%, 0 100%);
                content: "";
                height: min(28cqw, 350px);
                left: -1px;
                mix-blend-mode: multiply;
                pointer-events: none;
                position: absolute;
                width: min(27cqw, 340px);
                z-index: 2;
            }

            [data-web-template="torque-service"] [data-document-role="heroImage"] {
                inset: 0;
                position: absolute;
            }

            [data-web-template="torque-service"] [data-document-role="heroImage"] .site-builder-image {
                filter: grayscale(100%) contrast(1.08);
                height: 100% !important;
                max-width: none;
                object-fit: cover;
                width: 100% !important;
            }

            [data-web-template="torque-service"] [data-document-role="heroFloatingProof"] {
                bottom: clamp(1.25rem, 3vw, 2.25rem);
                border-left: 4px solid var(--site-accent) !important;
                position: absolute;
                right: clamp(1.25rem, 3vw, 2.25rem);
                z-index: 4;
            }

            [data-web-template="torque-service"] [data-web-region="homeTrust"] {
                padding-inline: clamp(1.25rem, 2vw, 2rem);
            }

            [data-web-template="torque-service"] [data-web-region="homeTrust"] > .site-builder-grid {
                gap: 0 !important;
            }

            [data-web-template="torque-service"] [data-web-region="homeTrust"] .site-builder-card:hover {
                border-color: color-mix(in srgb, var(--site-surface) 18%, transparent) !important;
                box-shadow: none;
                transform: none;
            }

            [data-web-template="torque-service"] [data-document-role="editorialVisual"] {
                clip-path: polygon(0 0, 100% 0, 100% calc(100% - 46px), calc(100% - 46px) 100%, 0 100%);
                overflow: hidden;
                position: relative;
            }

            [data-web-template="torque-service"] [data-document-role="editorialVisual"] .site-builder-image,
            [data-web-template="torque-service"] [data-web-region="tiresHero"] [data-document-role="heroVisual"] .site-builder-image {
                filter: grayscale(100%) contrast(1.05);
                height: 100% !important;
                object-fit: cover;
                transition: filter 500ms cubic-bezier(.2,.7,.2,1), transform 700ms cubic-bezier(.2,.7,.2,1);
                width: 100% !important;
            }

            [data-web-template="torque-service"] [data-document-role="editorialVisual"]:hover .site-builder-image,
            [data-web-template="torque-service"] [data-web-region="tiresHero"] [data-document-role="heroVisual"]:hover .site-builder-image {
                filter: grayscale(0%) contrast(1);
                transform: scale(1.025);
            }

            [data-web-template="torque-service"] [data-web-region="homeServices"] .site-builder-card,
            [data-web-template="torque-service"] [data-web-region="servicesOverview"] .site-builder-card,
            [data-web-template="torque-service"] [data-web-region="tiresServices"] .site-builder-card {
                border-top: 4px solid var(--site-primary) !important;
            }

            [data-web-template="torque-service"] [data-web-region="homeServices"] .site-builder-card:hover,
            [data-web-template="torque-service"] [data-web-region="servicesOverview"] .site-builder-card:hover,
            [data-web-template="torque-service"] [data-web-region="tiresServices"] .site-builder-card:hover {
                border-top-color: var(--site-accent) !important;
            }

            [data-web-template="torque-service"] [data-web-region="homeProcess"] .site-builder-card,
            [data-web-template="torque-service"] [data-web-region="servicesProcess"] .site-builder-card {
                box-shadow: none;
            }

            [data-web-template="torque-service"] [data-web-region="homeProcess"] .site-builder-card:hover,
            [data-web-template="torque-service"] [data-web-region="servicesProcess"] .site-builder-card:hover {
                box-shadow: none;
                transform: translateY(-4px);
            }

            [data-web-template="torque-service"] [data-web-region="servicesPrice"] .site-builder-card {
                border-bottom: 4px solid var(--site-accent) !important;
            }

            [data-web-template="torque-service"] [data-site-builder-block="accordion"] {
                border-top: 1px solid var(--site-border);
            }

            [data-web-template="torque-service"] [data-site-builder-block="accordionItem"] {
                border-bottom: 1px solid var(--site-border);
            }

            [data-web-template="torque-service"] [data-web-region$="Cta"] .site-builder-button,
            [data-web-template="torque-service"] [data-web-region="homeCta"] .site-builder-button {
                width: 100% !important;
            }

            [data-web-template="torque-service"] [data-web-region="mobileAction"] {
                display: none;
            }

            [data-web-template="torque-service"] [data-web-region="footer"] .site-builder-navigation {
                align-items: flex-start;
                flex-direction: column;
                gap: .7rem;
            }

            [data-web-template="torque-service"] [data-web-region="footer"] {
                color: var(--site-surface);
            }

            [data-web-template="torque-service"] [data-web-region="footer"] a {
                transition: color 180ms ease;
            }

            [data-web-template="torque-service"] [data-web-region="footer"] a:hover {
                color: var(--site-accent) !important;
            }

            [data-web-template="torque-service"]:not([data-web-template-editor="true"])[data-torque-runtime-ready] [data-torque-reveal]:not([data-torque-revealed]) {
                opacity: 0;
                transform: translateY(26px);
            }

            [data-web-template="torque-service"]:not([data-web-template-editor="true"])[data-torque-runtime-ready] [data-torque-reveal] {
                opacity: 1;
                transform: translateY(0);
                transition: opacity 700ms cubic-bezier(.2,.7,.2,1), transform 700ms cubic-bezier(.2,.7,.2,1);
                will-change: opacity, transform;
            }

            [data-web-template="torque-service"] .torque-mobile-menu-toggle {
                display: none;
            }

            @container (max-width: 900px) {
                [data-web-template="torque-service"] {
                    padding-bottom: 72px;
                }

                [data-web-template="torque-service"] .site-builder-grid[data-site-builder-stack-on-mobile="true"] {
                    grid-auto-rows: auto !important;
                    grid-template-columns: minmax(0, 1fr) !important;
                    grid-template-rows: none !important;
                }

                [data-web-template="torque-service"] .site-builder-grid[data-site-builder-stack-on-mobile="true"] > .site-builder-grid-item {
                    grid-column: 1 !important;
                    grid-row: auto !important;
                }

                [data-web-template="torque-service"] [data-web-region="serviceStrip"] > .site-builder-grid {
                    grid-template-columns: minmax(0, 1fr) auto !important;
                }

                [data-web-template="torque-service"] [data-web-region="serviceStrip"] > .site-builder-grid > .site-builder-grid-item {
                    grid-column: auto !important;
                    grid-row: 1 !important;
                }

                [data-web-template="torque-service"] [data-web-region="header"] {
                    min-height: 74px;
                    padding-inline: 1rem;
                }

                [data-web-template="torque-service"] [data-web-region="header"] > .site-builder-grid {
                    grid-template-columns: minmax(0, 1fr) auto !important;
                }

                [data-web-template="torque-service"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="brand"]) {
                    display: block;
                    grid-column: 1 !important;
                    grid-row: 1 !important;
                }

                [data-web-template="torque-service"] [data-document-role="brand"] {
                    margin-left: -1rem;
                    max-width: 220px;
                    min-height: 74px !important;
                }

                [data-web-template="torque-service"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]),
                [data-web-template="torque-service"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    display: none;
                }

                [data-web-template="torque-service"] [data-web-region="header"] .torque-mobile-menu-toggle {
                    align-items: center;
                    background: var(--site-surface);
                    border: 1px solid var(--site-border);
                    color: var(--site-primary);
                    cursor: pointer;
                    display: grid;
                    gap: 5px;
                    height: 44px;
                    padding: 10px;
                    place-content: center;
                    position: absolute;
                    right: 1rem;
                    top: 15px;
                    width: 44px;
                    z-index: 4;
                }

                [data-web-template="torque-service"] .torque-mobile-menu-toggle > span {
                    background: currentColor;
                    display: block;
                    height: 2px;
                    transition: opacity 180ms ease, transform 180ms ease;
                    width: 23px;
                }

                [data-web-template="torque-service"] [data-web-region="header"][data-torque-mobile-menu-open] {
                    background: var(--site-surface) !important;
                    bottom: 0;
                    color: var(--site-primary);
                    left: 0;
                    overflow-y: auto;
                    padding-inline: 1rem;
                    position: fixed;
                    right: 0;
                    top: 0;
                    z-index: 80;
                }

                [data-web-template="torque-service"] [data-web-region="header"][data-torque-mobile-menu-open] > .site-builder-grid {
                    align-content: start;
                    min-height: 100%;
                    padding-bottom: 2rem;
                }

                [data-web-template="torque-service"] [data-web-region="header"][data-torque-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]),
                [data-web-template="torque-service"] [data-web-region="header"][data-torque-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    display: block;
                    grid-column: 1 / -1 !important;
                }

                [data-web-template="torque-service"] [data-web-region="header"][data-torque-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]) {
                    grid-row: 2 !important;
                    margin-top: clamp(3rem, 9vh, 5rem);
                }

                [data-web-template="torque-service"] [data-web-region="header"][data-torque-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    grid-row: 3 !important;
                }

                [data-web-template="torque-service"] [data-web-region="header"][data-torque-mobile-menu-open] [data-document-role="headerNavigation"],
                [data-web-template="torque-service"] [data-web-region="header"][data-torque-mobile-menu-open] [data-document-role="headerActions"] {
                    align-items: stretch;
                    flex-direction: column;
                    gap: 0 !important;
                }

                [data-web-template="torque-service"] [data-web-region="header"][data-torque-mobile-menu-open] [data-document-role="headerNavigation"] > [data-site-builder-block="text"] {
                    border-bottom: 1px solid var(--site-border);
                    padding-block: 1rem;
                    width: 100%;
                }

                [data-web-template="torque-service"] [data-web-region="header"][data-torque-mobile-menu-open] [data-document-role="headerNavigation"] .site-builder-text {
                    color: var(--site-primary) !important;
                    display: block;
                    font-family: var(--site-heading-font) !important;
                    font-size: clamp(2rem, 9vw, 3.1rem) !important;
                    font-weight: 900 !important;
                    letter-spacing: -.025em;
                    line-height: 1 !important;
                    text-transform: uppercase;
                    width: 100% !important;
                }

                [data-web-template="torque-service"] [data-web-region="header"][data-torque-mobile-menu-open] [data-document-role="headerActions"] {
                    gap: .75rem !important;
                    margin-top: 1rem;
                }

                [data-web-template="torque-service"] [data-web-region="header"][data-torque-mobile-menu-open] [data-document-role="headerActions"] > *,
                [data-web-template="torque-service"] [data-web-region="header"][data-torque-mobile-menu-open] [data-document-role="headerCta"] {
                    width: 100% !important;
                }

                [data-web-template="torque-service"] [data-web-region="header"][data-torque-mobile-menu-open] .torque-mobile-menu-toggle > span:first-child {
                    transform: translateY(7px) rotate(45deg);
                }

                [data-web-template="torque-service"] [data-web-region="header"][data-torque-mobile-menu-open] .torque-mobile-menu-toggle > span:nth-child(2) {
                    opacity: 0;
                }

                [data-web-template="torque-service"] [data-web-region="header"][data-torque-mobile-menu-open] .torque-mobile-menu-toggle > span:last-child {
                    transform: translateY(-7px) rotate(-45deg);
                }

                [data-web-template="torque-service"]:has([data-torque-mobile-menu-open]) [data-web-region="mobileAction"] {
                    display: none;
                }

                [data-web-template="torque-service"] [data-web-region="homeHero"] > .site-builder-grid {
                    grid-template-columns: minmax(0, 1fr) !important;
                }

                [data-web-template="torque-service"] [data-web-region="homeHero"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="heroCopy"]),
                [data-web-template="torque-service"] [data-web-region="homeHero"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="heroVisual"]) {
                    grid-column: 1 !important;
                    grid-row: auto !important;
                }

                [data-web-template="torque-service"] [data-document-role="heroCopy"] {
                    padding: clamp(3.5rem, 9vw, 5rem) 1.25rem !important;
                }

                [data-web-template="torque-service"] [data-document-role="heroVisual"] {
                    min-height: 580px !important;
                }

                [data-web-template="torque-service"] [data-web-region="homeTrust"] > .site-builder-grid {
                    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
                }

                [data-web-template="torque-service"] [data-web-region="homeTrust"] > .site-builder-grid > .site-builder-grid-item {
                    grid-column: auto !important;
                    grid-row: auto !important;
                }

                [data-web-template="torque-service"] [data-web-region="mobileAction"] {
                    bottom: 0;
                    display: block;
                    left: 0;
                    padding: 8px;
                    position: fixed;
                    right: 0;
                    z-index: 75;
                }

                [data-web-template="torque-service"] [data-web-region="mobileAction"] > .site-builder-grid {
                    gap: 2px !important;
                    grid-auto-rows: auto !important;
                    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
                }

                [data-web-template="torque-service"] [data-web-region="mobileAction"] > .site-builder-grid > .site-builder-grid-item {
                    grid-column: auto !important;
                    grid-row: 1 !important;
                }

                [data-web-template="torque-service"] [data-web-region="mobileAction"] .site-builder-button {
                    min-height: 50px;
                    width: 100% !important;
                }
            }

            @container (max-width: 640px) {
                [data-web-template="torque-service"] .site-builder-section,
                [data-web-template="torque-service"] [data-site-builder-block="footer"] {
                    padding-inline: 1rem;
                }

                [data-web-template="torque-service"] [data-web-region="serviceStrip"] {
                    padding-inline: 1rem;
                }

                [data-web-template="torque-service"] [data-web-region="serviceStrip"] > .site-builder-grid > .site-builder-grid-item:last-child {
                    display: none;
                }

                [data-web-template="torque-service"] [data-web-region="serviceStrip"] > .site-builder-grid {
                    grid-template-columns: minmax(0, 1fr) !important;
                }

                [data-web-template="torque-service"] [data-document-role="heroCopy"]:has(> [data-document-role="heroHeading"]) {
                    grid-template-columns: minmax(0, 1fr);
                }

                [data-web-template="torque-service"] [data-document-role="heroCopy"] > * {
                    grid-column: 1 !important;
                }

                [data-web-template="torque-service"] [data-document-role="heroPrimaryAction"],
                [data-web-template="torque-service"] [data-document-role="heroSecondaryAction"] {
                    width: 100% !important;
                }

                [data-web-template="torque-service"] [data-document-role="heroVisual"] {
                    min-height: 480px !important;
                }

                [data-web-template="torque-service"] [data-document-role="heroFloatingProof"] {
                    bottom: 1rem;
                    left: 1rem;
                    max-width: none !important;
                    right: 1rem;
                }

                [data-web-template="torque-service"] [data-web-region="homeTrust"] > .site-builder-grid {
                    grid-template-columns: minmax(0, 1fr) !important;
                }

                [data-web-template="torque-service"] [data-web-region="homeTrust"] .site-builder-card {
                    border-bottom: 1px solid color-mix(in srgb, var(--site-surface) 18%, transparent) !important;
                    border-right: 0 !important;
                    min-height: 118px !important;
                }

                [data-web-template="torque-service"] [data-web-region$="Cta"] .site-builder-grid-item,
                [data-web-template="torque-service"] [data-web-region="homeCta"] .site-builder-grid-item {
                    grid-column: 1 !important;
                    grid-row: auto !important;
                }

                [data-web-template="torque-service"] [data-web-region$="Cta"] .site-builder-button,
                [data-web-template="torque-service"] [data-web-region="homeCta"] .site-builder-button {
                    margin-top: .5rem;
                    width: 100% !important;
                }
            }

            @media (prefers-reduced-motion: reduce) {
                [data-web-template="torque-service"],
                [data-web-template="torque-service"] * {
                    scroll-behavior: auto !important;
                }

                [data-web-template="torque-service"] .site-builder-card,
                [data-web-template="torque-service"] .site-builder-button,
                [data-web-template="torque-service"] .site-builder-image,
                [data-web-template="torque-service"] [data-torque-reveal] {
                    transition-duration: .01ms !important;
                }
            }
        `}</style>
    );
}

function TorqueServiceFallbackPage({
    props,
    design,
    LinkComponent,
}: {
    props: TemplateRenderProps;
    design: ResolvedTemplateDesign;
    LinkComponent: TemplateLinkComponent;
}) {
    const page = props.page;

    if (!page) {
        return null;
    }

    return (
        <main className="px-5 py-20 sm:px-6" data-builder-document-region="page">
            <article className="mx-auto max-w-[900px] leading-8 text-[var(--site-muted)] [&_a]:text-[var(--site-accent)] [&_blockquote]:border-l-4 [&_blockquote]:border-[var(--site-accent)] [&_blockquote]:pl-5 [&_h1]:mb-7 [&_h1]:font-[var(--site-heading-font)] [&_h1]:text-6xl [&_h1]:font-black [&_h1]:uppercase [&_h1]:leading-[.82] [&_h1]:text-[var(--site-text)] [&_h2]:mb-3 [&_h2]:mt-10 [&_h2]:font-[var(--site-heading-font)] [&_h2]:text-4xl [&_h2]:font-black [&_h2]:uppercase [&_h2]:text-[var(--site-text)] [&_li]:mb-2 [&_ol]:mb-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:mb-5 [&_strong]:text-[var(--site-text)] [&_ul]:mb-6 [&_ul]:list-disc [&_ul]:pl-6">
                <h1>{page.title}</h1>
                {renderTemplateContent(
                    page,
                    design,
                    LinkComponent,
                    props.leadForm,
                    props.readOnly,
                )}
            </article>
        </main>
    );
}

export const torqueServiceTemplate: SiteTemplateDefinition = {
    manifest: {
        code: "torque-service",
        name: "Autoservis",
        type: "website",
        category: "automotive",
        description: "Výrazná servisní šablona pro autoservisy a pneuservisy s důrazem na rychlou orientaci, transparentní proces a snadné objednání.",
        previewImage: "/templates/torque-service/preview.jpg",
        defaults: webSystemTemplateDesignDefaults["torque-service"],
        pageTypes: {
            home: { label: "Autoservis homepage", slots: ["richContent"] },
            content: { label: "Autoservis content page", slots: ["richContent"] },
            contact: { label: "Autoservis contact page", slots: ["richContent"] },
        },
        editable: {
            colors: true,
            typography: true,
            radius: true,
            header: ["builder"],
            footer: ["builder"],
            buttons: ["solid", "outline"],
        },
    },
    render: (props: TemplateRenderProps) => {
        const design = mergeTemplateDesign(
            torqueServiceTemplate.manifest.defaults,
            props.designOverride ?? props.site?.templateDesign,
        );
        const LinkComponent = props.LinkComponent ?? DefaultTemplateLink;

        return (
            <div
                className="min-h-screen overflow-x-hidden bg-[var(--site-background)] font-[var(--site-body-font)] text-[var(--site-text)] antialiased"
                data-web-template="torque-service"
                data-web-template-editor={props.editorMode ? "true" : undefined}
                style={{
                    ...templateDesignToCssVars(design),
                    containerType: "inline-size",
                }}
            >
                <TorqueServiceStyles />
                <WebGlobalBuilderRegion
                    design={design}
                    LinkComponent={LinkComponent}
                    props={props}
                    region="header"
                >
                    <TorqueServiceMobileMenu
                        languageCode={props.page?.languageCode ?? props.site?.defaultLanguageCode}
                    />
                </WebGlobalBuilderRegion>
                <WebBuilderPageRegion
                    design={design}
                    fallback={(
                        <TorqueServiceFallbackPage
                            design={design}
                            LinkComponent={LinkComponent}
                            props={props}
                        />
                    )}
                    LinkComponent={LinkComponent}
                    props={props}
                />
                <WebGlobalBuilderRegion
                    design={design}
                    LinkComponent={LinkComponent}
                    props={props}
                    region="footer"
                />
            </div>
        );
    },
};
